import { useState } from "react";
import { Plus, Pencil, Trash2, X, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { DatePicker } from "@/components/ui/DatePicker";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { PageLoader } from "@/components/ui/Spinner";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import {
  useMultisportEntries,
  useCreateMultisportEntry,
  useUpdateMultisportEntry,
  useDeleteMultisportEntry,
} from "../hooks/useMultisport";
import type { MultisportEntry, MultisportEntryPayload } from "../types";

// ── helpers ───────────────────────────────────────────────────────────────────

const fmt = (d: string | null | undefined) =>
  d
    ? new Date(d + "T00:00:00").toLocaleDateString("pl-PL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "—";

const fmtPrice = (p: string) =>
  parseFloat(p).toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: "always",
  }) + " zł";

// ── empty form factory ────────────────────────────────────────────────────────

function emptyForm(): FormState {
  return {
    employee: "",
    start_date: "",
    end_date: "",
    price: "",
    companions: [],
    package: "",
  };
}

interface FormState {
  employee: string;
  start_date: string;
  end_date: string;
  price: string;
  companions: string[];
  package: string;
}

interface FormErrors {
  employee?: string;
  start_date?: string;
  price?: string;
}

// ── companion list editor ─────────────────────────────────────────────────────

function CompanionsEditor({
  companions,
  onChange,
}: {
  companions: string[];
  onChange: (list: string[]) => void;
}) {
  const add = () => onChange([...companions, ""]);
  const remove = (i: number) => onChange(companions.filter((_, idx) => idx !== i));
  const update = (i: number, val: string) => {
    const next = [...companions];
    next[i] = val;
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
        Osoby towarzyszące
      </span>
      <div className="flex flex-col gap-2">
        {companions.map((c, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input
              type="text"
              value={c}
              onChange={(e) => update(i, e.target.value)}
              placeholder={`Osoba ${i + 1}`}
              className="input-base flex-1"
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={add}
          className="text-xs font-mono text-[var(--color-primary)] hover:underline text-left w-fit"
        >
          + Dodaj osobę
        </button>
      </div>
    </div>
  );
}

// ── main page ─────────────────────────────────────────────────────────────────

export function MultisportPage() {
  const { data: entries, isLoading } = useMultisportEntries();
  const { data: users = [] } = useInternalUsers();
  const createEntry = useCreateMultisportEntry();
  const updateEntry = useUpdateMultisportEntry();
  const deleteEntry = useDeleteMultisportEntry();

  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MultisportEntry | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [errors, setErrors] = useState<FormErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<MultisportEntry | null>(null);

  const existingEmployeeIds = new Set((entries ?? []).map((e) => e.employee));

  const userOptions = users.map((u) => ({
    value: u.id,
    label: u.full_name || `${u.first_name} ${u.last_name}`.trim() || u.username,
  }));

  function openAdd() {
    setEditTarget(null);
    setForm(emptyForm());
    setErrors({});
    setServerError(null);
    setModalOpen(true);
  }

  function openEdit(entry: MultisportEntry) {
    setEditTarget(entry);
    setForm({
      employee: entry.employee,
      start_date: entry.start_date,
      end_date: entry.end_date ?? "",
      price: entry.price,
      companions: [...entry.companions],
      package: entry.package,
    });
    setErrors({});
    setServerError(null);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditTarget(null);
  }

  function validate(): boolean {
    const e: FormErrors = {};
    if (!form.employee) e.employee = "Wybierz pracownika.";
    else if (!editTarget && existingEmployeeIds.has(form.employee))
      e.employee = "Ten pracownik ma już przypisaną kartę Multisport.";
    if (!form.start_date) e.start_date = "Podaj datę początkową.";
    if (!form.price || isNaN(parseFloat(form.price)))
      e.price = "Podaj poprawną cenę.";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    const payload: MultisportEntryPayload = {
      employee: form.employee,
      start_date: form.start_date,
      end_date: form.end_date || null,
      price: form.price,
      companions: form.companions.filter((c) => c.trim() !== ""),
      package: form.package,
    };

    try {
      setServerError(null);
      if (editTarget) {
        await updateEntry.mutateAsync({ id: editTarget.id, payload });
      } else {
        await createEntry.mutateAsync(payload);
      }
      closeModal();
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { errors?: { employee?: string[]; detail?: string } } } })
          ?.response?.data?.errors;
      if (detail?.employee) {
        setErrors((prev) => ({ ...prev, employee: detail.employee![0] }));
      } else {
        setServerError("Wystąpił błąd. Spróbuj ponownie.");
      }
    }
  }

  async function handleDelete(entry: MultisportEntry) {
    await deleteEntry.mutateAsync(entry.id);
    setDeleteConfirm(null);
  }

  if (isLoading) return <PageLoader />;

  const isSubmitting = createEntry.isPending || updateEntry.isPending;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-mono font-bold uppercase tracking-wider text-[var(--color-text-primary)]">
            Multisport
          </h1>
          <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
            Karty Multisport pracowników
          </p>
        </div>
        <Button onClick={openAdd} size="sm">
          <Plus size={14} />
          Dodaj kartę Multisport
        </Button>
      </div>

      {/* Table */}
      {!entries || entries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-[var(--color-text-muted)]">
          <UserCheck size={40} className="mb-3 opacity-30" />
          <p className="text-sm font-mono">Brak kart Multisport</p>
          <p className="text-xs mt-1">Dodaj pierwszą kartę klikając przycisk powyżej.</p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-[var(--color-border)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-subtle)]">
                <th className="px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
                  Pracownik
                </th>
                <th className="px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
                  Od
                </th>
                <th className="px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
                  Do
                </th>
                <th className="px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
                  Cena
                </th>
                <th className="px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
                  Pakiet
                </th>
                <th className="px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
                  Osoby towarzyszące
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr
                  key={entry.id}
                  className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-subtle)] transition-colors"
                >
                  <td className="px-4 py-3 font-medium text-[var(--color-text-primary)]">
                    {entry.employee_name}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {fmt(entry.start_date)}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {fmt(entry.end_date)}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)] font-mono">
                    {fmtPrice(entry.price)}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {entry.package || "—"}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {entry.companions.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {entry.companions.map((c, i) => (
                          <span
                            key={i}
                            className="inline-block px-2 py-0.5 text-xs bg-[var(--color-bg-subtle)] border border-[var(--color-border)] rounded"
                          >
                            {c}
                          </span>
                        ))}
                      </div>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => openEdit(entry)}
                        className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
                        title="Edytuj"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(entry)}
                        className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
                        title="Usuń"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit modal */}
      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editTarget ? "Edytuj kartę Multisport" : "Dodaj kartę Multisport"}
        size="md"
      >
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <SearchableSelect
            label="Pracownik"
            options={userOptions}
            value={form.employee}
            placeholder="Wybierz pracownika..."
            onChange={(e) => {
              setForm((f) => ({ ...f, employee: e.target.value }));
              setErrors((err) => ({ ...err, employee: undefined }));
            }}
            disabled={!!editTarget}
            error={errors.employee}
          />

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label htmlFor="multisport-start-date" className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
                Od
              </label>
              <DatePicker
                id="multisport-start-date"
                value={form.start_date}
                onChange={(v) => {
                  setForm((f) => ({ ...f, start_date: v }));
                  setErrors((err) => ({ ...err, start_date: undefined }));
                }}
                className={errors.start_date ? "border-[var(--color-primary)]" : undefined}
              />
              {errors.start_date && (
                <p className="text-xs font-mono text-[var(--color-primary)]">{errors.start_date}</p>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="multisport-end-date" className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
                Do (opcjonalnie)
              </label>
              <DatePicker
                id="multisport-end-date"
                value={form.end_date}
                onChange={(v) => setForm((f) => ({ ...f, end_date: v }))}
              />
            </div>
          </div>

          <Input
            label="Cena (zł)"
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={form.price}
            onChange={(e) => {
              setForm((f) => ({ ...f, price: e.target.value }));
              setErrors((err) => ({ ...err, price: undefined }));
            }}
            error={errors.price}
          />

          <Input
            label="Pakiet"
            type="text"
            placeholder="np. Sport, Premium, Classic..."
            value={form.package}
            onChange={(e) => setForm((f) => ({ ...f, package: e.target.value }))}
          />

          <CompanionsEditor
            companions={form.companions}
            onChange={(list) => setForm((f) => ({ ...f, companions: list }))}
          />

          {serverError && (
            <p className="text-xs font-mono text-[var(--color-primary)]">{serverError}</p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" size="sm" onClick={closeModal}>
              Anuluj
            </Button>
            <Button type="submit" size="sm" loading={isSubmitting}>
              {editTarget ? "Zapisz zmiany" : "Dodaj"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Usuń kartę Multisport"
        size="sm"
      >
        <p className="text-sm text-[var(--color-text-secondary)] mb-6">
          Czy na pewno chcesz usunąć kartę Multisport dla{" "}
          <strong className="text-[var(--color-text-primary)]">
            {deleteConfirm?.employee_name}
          </strong>
          ?
        </p>
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setDeleteConfirm(null)}
          >
            Anuluj
          </Button>
          <Button
            variant="danger"
            size="sm"
            loading={deleteEntry.isPending}
            onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
          >
            Usuń
          </Button>
        </div>
      </Modal>
    </div>
  );
}
